import React, { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { IClinicianProfile, IClinicianShift, IShiftTimes, IDoc } from "../interfaces/types";
import useApiService from "../hooks/useApiService";
import { colors, semanticColors } from "../theme";
import StatCard from "../pages/dashboard/stat-card";
import {
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

interface ShiftModalProps {
  open: boolean;
  handleClose: () => void;
  shift?: IClinicianShift;
  clinicianProfile?: IClinicianProfile | null;
  onUpdateShift: (id: string, updatedShift: IShiftTimes) => void;
}

const ShiftModal: React.FC<ShiftModalProps> = ({
  open,
  handleClose,
  shift,
  clinicianProfile,
  onUpdateShift,
}) => {
  const api = useApiService();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    if (open) {
      window.addEventListener("keydown", handleEsc);
      modalRef.current?.focus();
    }
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, handleClose]);

  if (!open) return null;

  const profileData = clinicianProfile?.people;
  const reliability = typeof clinicianProfile?.reliability === 'string' 
    ? clinicianProfile.reliability 
    : (clinicianProfile?.reliability || 0);
  const documentStatusSummary = clinicianProfile?.document_status_summary;

  const getStatusColor = (status: string | undefined): string => {
    switch (status?.toLowerCase()) {
      case "approved":
        return `background-color: ${semanticColors.badge.success.background}; color: ${semanticColors.badge.success.text}; border-color: ${semanticColors.badge.success.border}`;
      case "pending":
        return `background-color: ${semanticColors.badge.warning.background}; color: ${semanticColors.badge.warning.text}; border-color: ${semanticColors.badge.warning.border}`;
      case "rejected":
        return `background-color: ${semanticColors.badge.error.background}; color: ${semanticColors.badge.error.text}; border-color: ${semanticColors.badge.error.border}`;
      case "missing":
        return `background-color: ${semanticColors.background.tertiary}; color: ${semanticColors.text.tertiary}; border-color: ${colors.borderMedium}`;
      default:
        return `background-color: ${semanticColors.background.tertiary}; color: ${semanticColors.text.tertiary}; border-color: ${colors.borderMedium}`;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className="relative flex h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl shadow-2xl sm:h-[90vh]"
        style={{ backgroundColor: semanticColors.card.background }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="shrink-0 rounded-t-2xl px-4 py-3 text-white sm:px-6 sm:py-4" style={{ background: semanticColors.background.gradient }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold sm:text-xl">Clinician Profile</h1>
            </div>
            <div className="flex items-center gap-4">
              
              <button
                onClick={handleClose}
                className="rounded-full p-2 text-white/80 transition-all hover:bg-white/10 hover:text-white"
              >
                <svg className="size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="min-h-0 p-3 sm:p-4 lg:p-6">
            {clinicianProfile && profileData ? (
              <div className="space-y-4 sm:space-y-6">
              {/* Clinician Profile Header */}
              <div className="rounded-2xl border p-4 sm:p-6" style={{ backgroundColor: colors.secondaryLight, borderColor: semanticColors.card.border }}>
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
                  <div className="flex size-16 items-center justify-center rounded-2xl text-2xl font-bold text-white shadow-lg" 
                       style={{ background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)` }}>
                    {`${profileData.firstname?.charAt(0) ?? ""}${profileData.lastname?.charAt(0) ?? ""}`}
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                      <h2 className="text-xl font-bold sm:text-2xl" style={{ color: semanticColors.text.primary }}>
                        {`${profileData.firstname ?? ""} ${profileData.lastname ?? ""}`}
                      </h2>
                      {shift?.position_title && (
                        <span className="text-xl font-bold sm:text-1xl" style={{ color: semanticColors.text.primary }}>
                         ({shift.position_title})
                        </span>
                      )}
                    </div>
                    <p className="mb-3 text-sm font-medium" style={{ color: colors.secondaryDark }}>
                      {profileData.email || "No email provided"}
                    </p>
                    <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
                      <div className="flex items-center gap-2">
                        <div className="flex size-6 items-center justify-center rounded-lg" 
                             style={{ backgroundColor: semanticColors.badge.success.background }}>
                          <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" 
                               style={{ color: semanticColors.badge.success.text }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: semanticColors.badge.success.text }}>
                          {clinicianProfile?.is_approved ? "Approved" : "Pending Approval"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex size-6 items-center justify-center rounded-lg" 
                             style={{ backgroundColor: semanticColors.badge.info.background }}>
                          <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" 
                               style={{ color: semanticColors.badge.info.text }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: semanticColors.badge.info.text }}>
                          {reliability}% Reliability
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border p-4 sm:p-6" style={{ backgroundColor: semanticColors.card.background, borderColor: semanticColors.card.border }}>
                  <div className="mb-3 flex items-center gap-3 sm:mb-4">
                    <div className="flex size-8 items-center justify-center rounded-xl" 
                         style={{ backgroundColor: semanticColors.badge.info.text }}>
                      <svg className="size-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold sm:text-lg" style={{ color: semanticColors.text.primary }}>Contact Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm" style={{ color: semanticColors.text.secondary }}>Phone</p>
                      <p className="font-semibold" style={{ color: semanticColors.text.primary }}>
                        {profileData.phone || "Not provided"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: semanticColors.text.secondary }}>Email</p>
                      <p className="font-semibold" style={{ color: semanticColors.text.primary }}>
                        {profileData.email || "Not provided"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: semanticColors.text.secondary }}>Address</p>
                      <p className="font-semibold" style={{ color: semanticColors.text.primary }}>
                        {profileData.address1 ? `${profileData.address1}, ${profileData.city}, ${profileData.state} ${profileData.zip}` : "Not provided"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div className="rounded-2xl border p-4 sm:p-6" style={{ backgroundColor: semanticColors.card.background, borderColor: semanticColors.card.border }}>
                  <div className="mb-3 flex items-center gap-3 sm:mb-4">
                    <div className="flex size-8 items-center justify-center rounded-xl" 
                         style={{ backgroundColor: semanticColors.badge.success.text }}>
                      <svg className="size-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold sm:text-lg" style={{ color: semanticColors.text.primary }}>Performance</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-xl p-3 text-center" style={{ backgroundColor: semanticColors.badge.success.background }}>
                      <p className="text-2xl font-bold" style={{ color: semanticColors.badge.success.text }}>
                        {clinicianProfile?.completed_shifts ?? 0}
                      </p>
                      <p className="text-sm font-medium" style={{ color: semanticColors.badge.success.border }}>
                        Completed Shifts
                      </p>
                    </div>
                    <div className="rounded-xl p-3 text-center" style={{ backgroundColor: semanticColors.badge.info.background }}>
                      <p className="text-2xl font-bold" style={{ color: semanticColors.badge.info.text }}>
                        {clinicianProfile?.total_hours ?? 0}
                      </p>
                      <p className="text-sm font-medium" style={{ color: semanticColors.badge.info.border }}>
                        Total Hours
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Compliance */}
              {documentStatusSummary && (
                <div className="rounded-2xl border p-4 sm:p-6" style={{ backgroundColor: semanticColors.card.background, borderColor: semanticColors.card.border }}>
                  <div className="mb-4 flex items-center justify-between sm:mb-6">
                    <div className="flex items-center gap-3">
                      <div className="flex size-8 items-center justify-center rounded-xl" 
                           style={{ backgroundColor: semanticColors.badge.success.text }}>
                        <svg className="size-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <h3 className="text-base font-bold sm:text-lg" style={{ color: semanticColors.text.primary }}>Document Compliance</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: semanticColors.badge.success.text }}>
                        {documentStatusSummary.mandatory_total > 0 
                          ? Math.round((documentStatusSummary.mandatory_completed / documentStatusSummary.mandatory_total) * 100)
                          : 0}%
                      </p>
                      <p className="text-sm" style={{ color: semanticColors.text.tertiary }}>Complete</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="h-3 w-full overflow-hidden rounded-full" style={{ backgroundColor: semanticColors.background.tertiary }}>
                      <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{ 
                          width: `${documentStatusSummary.mandatory_total > 0 
                            ? (documentStatusSummary.mandatory_completed / documentStatusSummary.mandatory_total) * 100 
                            : 0}%`,
                          background: `linear-gradient(90deg, ${semanticColors.badge.success.text} 0%, ${semanticColors.badge.success.border} 100%)`
                        }}
                      />
                    </div>
                  </div>

                  {/* Status Grid */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                    <div className="group relative overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.02] hover:-translate-y-1">
                      <div className="h-1 w-full bg-green-500"></div>
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
                              <p className="text-xs font-bold tracking-wider uppercase text-green-600">VERIFIED</p>
                            </div>
                            <p className="text-2xl font-bold text-green-600 mb-1">
                              {documentStatusSummary.verified ?? 0}
                            </p>
                            <p className="text-xs text-gray-500">Documents verified and approved.</p>
                          </div>
                          <div className="ml-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-100">
                            <CheckBadgeIcon className="h-6 w-6 text-green-600" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group relative overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.02] hover:-translate-y-1">
                      <div className="h-1 w-full bg-yellow-500"></div>
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-yellow-500"></div>
                              <p className="text-xs font-bold tracking-wider uppercase text-yellow-600">PENDING</p>
                            </div>
                            <p className="text-2xl font-bold text-yellow-600 mb-1">
                              {documentStatusSummary.pending ?? 0}
                            </p>
                            <p className="text-xs text-gray-500">Documents under review.</p>
                          </div>
                          <div className="ml-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-yellow-100">
                            <ClockIcon className="h-6 w-6 text-yellow-600" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group relative overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.02] hover:-translate-y-1">
                      <div className="h-1 w-full bg-red-500"></div>
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-red-500"></div>
                              <p className="text-xs font-bold tracking-wider uppercase text-red-600">REJECTED</p>
                            </div>
                            <p className="text-2xl font-bold text-red-600 mb-1">
                              {documentStatusSummary.rejected ?? 0}
                            </p>
                            <p className="text-xs text-gray-500">Documents that need revision.</p>
                          </div>
                          <div className="ml-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-100">
                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="group relative overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.02] hover:-translate-y-1">
                      <div className="h-1 w-full bg-gray-400"></div>
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-gray-400"></div>
                              <p className="text-xs font-bold tracking-wider uppercase text-gray-600">MISSING</p>
                            </div>
                            <p className="text-2xl font-bold text-gray-600 mb-1">
                              {documentStatusSummary.missing ?? 0}
                            </p>
                            <p className="text-xs text-gray-500">Required documents not uploaded.</p>
                          </div>
                          <div className="ml-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100">
                            <NoSymbolIcon className="h-6 w-6 text-gray-600" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents List */}
              {clinicianProfile?.documents && clinicianProfile.documents.length > 0 && (
                <div className="rounded-2xl border p-4 sm:p-6" style={{ backgroundColor: semanticColors.card.background, borderColor: semanticColors.card.border }}>
                  <div className="mb-4 flex items-center justify-between sm:mb-6">
                    <div className="flex items-center gap-3">
                      <div className="flex size-8 items-center justify-center rounded-xl" 
                           style={{ backgroundColor: semanticColors.badge.info.text }}>
                        <svg className="size-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-base font-bold sm:text-lg" style={{ color: semanticColors.text.primary }}>
                        Documents ({clinicianProfile.documents.length})
                      </h3>
                    </div>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {clinicianProfile.documents.map((doc: IDoc) => (
                      <div
                        key={doc.document_key}
                        className="rounded-xl border p-3 sm:p-4 transition-all hover:shadow-sm"
                        style={{ backgroundColor: semanticColors.background.tertiary, borderColor: semanticColors.card.border }}
                      >
                        {/* Mobile-first responsive layout */}
                        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                          {/* Left section - Document info */}
                          <div className="flex items-start space-x-3 sm:flex-1">
                            {/* Document icon */}
                            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border" 
                                 style={{ backgroundColor: semanticColors.card.background, borderColor: semanticColors.card.border }}>
                              {doc.file_url ? (
                                <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" 
                                     style={{ color: semanticColors.badge.info.text }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              ) : (
                                <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" 
                                     style={{ color: semanticColors.text.tertiary }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                              )}
                            </div>
                            
                            {/* Document details */}
                            <div className="min-w-0 flex-1">
                              {/* Title and required badge */}
                              <div className="mb-1 flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
                                <p className="font-semibold text-sm sm:text-base truncate" style={{ color: semanticColors.text.primary }}>
                                  {doc.name}
                                </p>
                                {doc.is_mandatory && (
                                  <span className="inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-semibold" 
                                        style={{ backgroundColor: semanticColors.badge.error.background, color: semanticColors.badge.error.text }}>
                                    Required
                                  </span>
                                )}
                              </div>
                              
                              {/* Description */}
                              <p className="text-xs sm:text-sm mb-1" style={{ color: semanticColors.text.secondary }}>
                                {doc.description}
                              </p>
                              
                              {/* Upload date */}
                              {doc.uploaded_at && (
                                <p className="text-xs" style={{ color: semanticColors.text.tertiary }}>
                                  Uploaded: {dayjs(doc.uploaded_at).format("MMM D, YYYY")}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Right section - Actions and status */}
                          <div className="flex items-center justify-between sm:justify-end sm:space-x-2">
                            {/* View document link */}
                            {doc.file_url && (
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-lg p-2 transition-all hover:bg-white"
                                style={{ color: semanticColors.badge.info.text }}
                                title="View Document"
                              >
                                <svg className="size-4 sm:size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}
                            
                            {/* Status badge */}
                            <span
                              className="rounded-full border px-2 py-1 text-xs font-semibold sm:px-3"
                              style={{ 
                                backgroundColor: doc.current_status === 'approved' ? semanticColors.badge.success.background :
                                           doc.current_status === 'pending' ? semanticColors.badge.warning.background :
                                           doc.current_status === 'rejected' ? semanticColors.badge.error.background :
                                           semanticColors.background.tertiary,
                                color: doc.current_status === 'approved' ? semanticColors.badge.success.text :
                                       doc.current_status === 'pending' ? semanticColors.badge.warning.text :
                                       doc.current_status === 'rejected' ? semanticColors.badge.error.text :
                                       semanticColors.text.tertiary,
                                borderColor: doc.current_status === 'approved' ? semanticColors.badge.success.border :
                                            doc.current_status === 'pending' ? semanticColors.badge.warning.border :
                                            doc.current_status === 'rejected' ? semanticColors.badge.error.border :
                                            colors.borderMedium
                              }}
                            >
                              {doc.current_status?.toUpperCase() ?? "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            ) : (
              <div className="flex h-96 items-center justify-center rounded-2xl border" 
                   style={{ backgroundColor: semanticColors.background.tertiary, borderColor: semanticColors.card.border }}>
                {shift?.clinician_id ? (
                  <div className="text-center">
                    <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-4 border-t-transparent" 
                         style={{ borderColor: colors.borderMedium, borderTopColor: semanticColors.badge.info.text }} />
                    <p className="mb-2 text-lg font-semibold" style={{ color: semanticColors.text.primary }}>
                      Loading Clinician Details...
                    </p>
                    <p className="text-sm" style={{ color: semanticColors.text.secondary }}>
                      Please wait while we fetch the information
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl" 
                         style={{ backgroundColor: semanticColors.background.tertiary }}>
                      <svg className="size-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" 
                           style={{ color: semanticColors.text.tertiary }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h3 className="mb-2 text-xl font-bold" style={{ color: semanticColors.text.primary }}>
                      Unassigned Shift
                    </h3>
                    <p className="text-sm" style={{ color: semanticColors.text.secondary }}>
                      No clinician has been assigned to this shift yet.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftModal;